import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { MessageSquare, Clock, ChevronRight } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import GeneralChatDrawer from "@/components/chat/GeneralChatDrawer";
import placeholder from "@/assets/placeholder.png?url";

type ConversationWithDetails = {
  id: string;
  listing_id: string;
  renter_id: string;
  host_id: string;
  type: "inquiry" | "booking";
  booking_id?: string;
  last_message_at: string;
  last_message_preview?: string;
  last_message_sender?: string;
  created_at: string;
  listing: {
    title: string;
    photos: string[];
  } | null;
  renter: {
    name: string;
    photo_url?: string;
  } | null;
  host: {
    name: string;
    photo_url?: string;
  } | null;
  unread_count?: number;
};

type Props = {
  currentUserId: string;
};

export default function ConversationsList({ currentUserId }: Props) {
  const [selectedConversation, setSelectedConversation] = useState<ConversationWithDetails | null>(null);
  const [chatOpen, setChatOpen] = useState(false);

  const { data: conversations = [], isLoading, refetch } = useQuery({
    queryKey: ["conversations", currentUserId],
    queryFn: async (): Promise<ConversationWithDetails[]> => {
      const { data, error } = await supabase
        .from("conversations")
        .select(`
          id, type, listing_id, host_id, renter_id, booking_id,
          last_message_at, last_message_preview, last_message_sender, created_at,
          listing:listings!conversations_listing_id_fkey(id, title, photos),
          host:users!conversations_host_id_fkey(id, name, photo_url),
          renter:users!conversations_renter_id_fkey(id, name, photo_url)
        `)
        .or(`host_id.eq.${currentUserId},renter_id.eq.${currentUserId}`)
        .order("last_message_at", { ascending: false });

      if (error) throw error;

      // Get unread count for each conversation
      const conversationsWithDetails = await Promise.all(
        (data || []).map(async (conv: any) => {
          // Determine who the "other" user is
          const isHost = conv.host_id === currentUserId;
          const otherId = isHost ? conv.renter_id : conv.host_id;

          // Get unread count (messages from other user that haven't been read)
          const { count: unreadCount } = await supabase
            .from("messages")
            .select("*", { count: "exact", head: true })
            .eq("conversation_id", conv.id)
            .eq("sender_id", otherId)
            .is("read_at", null);

          return {
            ...conv,
            unread_count: unreadCount || 0,
          };
        })
      );

      return conversationsWithDetails as ConversationWithDetails[];
    },
    enabled: !!currentUserId,
  });

  const formatRelativeTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));

    if (diffInMinutes < 1) return "Lige nu";
    if (diffInMinutes < 60) return `${diffInMinutes}m`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}t`;
    return `${Math.floor(diffInMinutes / 1440)}d`;
  };

  const handleConversationClick = (conversation: ConversationWithDetails) => {
    setSelectedConversation(conversation);
    setChatOpen(true);
  };

  const handleChatClose = (open: boolean) => {
    setChatOpen(open);
    if (!open) {
      setSelectedConversation(null);
      // Refetch to update unread counts
      setTimeout(() => refetch(), 500);
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Beskeder</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center text-muted-foreground py-8">
            Indlæser beskeder...
          </div>
        </CardContent>
      </Card>
    );
  }

  if (conversations.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Beskeder</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center text-muted-foreground py-8">
            <MessageSquare className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>Ingen beskeder endnu</p>
            <p className="text-sm">Når nogen kontakter dig om dine opslag, vil de dukke op her</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className="overflow-hidden border-border/70 shadow-sm">
        <CardHeader className="border-b border-border/70 bg-card/60">
          <CardTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5" />
            Beskeder ({conversations.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="divide-y divide-border/70">
            {conversations.map((conversation) => {
              // Determine who the "other" user is
              const isHost = conversation.host_id === currentUserId;
              const otherUser = isHost ? conversation.renter : conversation.host;
              const otherAvatar = otherUser?.photo_url || placeholder;
              const otherName = otherUser?.name || "Bruger";
              const listingTitle = conversation.listing?.title || "Parkeringsplads";
              const listingPhoto = conversation.listing?.photos?.[0] || placeholder;

              return (
                <div
                  key={conversation.id}
                  className="group p-4 hover:bg-muted/45 cursor-pointer transition-colors"
                  onClick={() => handleConversationClick(conversation)}
                >
                  <div className="flex items-start gap-3">
                    <Avatar className="h-11 w-11 border border-border shadow-sm">
                      <AvatarImage
                        src={otherAvatar}
                        onError={(e) => {
                          if (e.currentTarget.src !== placeholder)
                            e.currentTarget.src = placeholder;
                        }}
                      />
                      <AvatarFallback>
                        {otherName[0] || "U"}
                      </AvatarFallback>
                    </Avatar>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-3 mb-1">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-sm truncate">
                            {otherName}
                          </span>
                          {conversation.type === "booking" && (
                            <Badge variant="secondary" className="text-[11px] h-5 px-2">
                              Booking
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          {(conversation.unread_count ?? 0) > 0 && (
                            <Badge variant="destructive" className="text-[11px] min-w-[20px] h-5 px-1.5 justify-center">
                              {conversation.unread_count}
                            </Badge>
                          )}
                          {conversation.last_message_at && (
                            <span className="text-xs text-muted-foreground flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {formatRelativeTime(conversation.last_message_at)}
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="text-sm font-medium text-foreground mb-1 truncate">
                        {listingTitle}
                      </div>

                      {conversation.last_message_preview && (
                        <div className="text-sm text-muted-foreground truncate leading-5">
                          {conversation.last_message_sender === currentUserId ? (
                            <span className="text-primary">Du: </span>
                          ) : null}
                          {conversation.last_message_preview}
                        </div>
                      )}
                    </div>

                    <div className="flex items-center gap-2 flex-shrink-0">
                    <div className="w-14 h-10 rounded-md overflow-hidden bg-muted border border-border/70 shadow-sm">
                      <img
                        src={listingPhoto}
                        alt={listingTitle}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          if (e.currentTarget.src !== placeholder)
                            e.currentTarget.src = placeholder;
                        }}
                      />
                    </div>
                    <ChevronRight className="h-4 w-4 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Chat Drawer */}
      {selectedConversation && (
        <GeneralChatDrawer
          open={chatOpen}
          onOpenChange={handleChatClose}
          listingId={selectedConversation.listing_id}
          listingTitle={selectedConversation.listing?.title || "Parkeringsplads"}
          hostId={selectedConversation.host_id}
          hostName={(selectedConversation.host_id === currentUserId ? selectedConversation.renter?.name : selectedConversation.host?.name) || undefined}
          hostAvatarUrl={(selectedConversation.host_id === currentUserId ? selectedConversation.renter?.photo_url : selectedConversation.host?.photo_url) || undefined}
          currentUserId={currentUserId}
          bookingId={selectedConversation.booking_id}
          conversationId={selectedConversation.id}
          renterId={selectedConversation.renter_id}
        />
      )}
    </>
  );
}